import { Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ActivatedRoute, Params } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import {
  selectItem,
  unlockShare
} from './share.actions';
import { IAppState } from '../app.state';

import {
  selectCurrentNote,
  selectCurrentShare,
  selectCurrentAttachments,
  selectText,
  selectChildren,
  selectShareCopy,
  selectPreview,
  selectTodos
} from './models';
import { ILoginState } from '../login/login.reducer';
import { selectVisual } from '../visual/visual.selector';
import { selectBranding } from '../branding/branding.selector';
import { getCurrentOrganization } from '../organizations/organizations.selector';
import { Meta } from '../../angular2-meta';
import { combineLatest } from 'rxjs';
import { selectCurrentProfile } from '../profile/profile.selector';
import { getCurrentNoteOrgWorkspace, getCurrentNoteOrgWorkspaces } from '../workspaces/workspaces.selector';
import { selectPreparedAllOutlines } from './note/outlines/outline.selector';

@Component({
  selector: 'nns-share-smart',
  template: `
    <div *ngIf="isNetworkError$ | async; then error; else noError"></div>
    <ng-template #error>
      <nns-error
        [errorCode]="isNetworkError$ | async"
        [currentOrganization]="currentOrganization$ | async"
        flex="noshrink"
      ></nns-error>
    </ng-template>
    <ng-template #noError>
      <div *ngIf="(currentNote$ | async)?.isEncrypted; then isEncryptred; else note"></div>
      <ng-template #isEncryptred>
        <nns-note-encrypted
          flex="noshrink"
          layout="row"
          layout-align="center center"
        ></nns-note-encrypted>
      </ng-template>
      <ng-template #note>
        <nns-share-item *ngIf="currentNote$ | async; let note"
          [note]="note"
          [attachments]="(attachments$ | async)"
          [workspaces]="(workspaces$ | async)"
          [currentNoteWorkspace]="(currentNoteWorkspace$ | async)"
          [children]="(children$ | async)"
          [preview]="(preview$ | async)"
          [login]="(login$ | async)"
          [share]="(share$ | async)"
          [shareCopy]="(shareCopy$ | async)"
          [text]="(text$ | async)"
          [todos]="(todos$ | async)"
          [visual]="(visual$ | async)"
          [isPending]="(isShareFetching$ | async)"
          [branding]="(branding$ | async)"
          [profile]="(profile$ | async)"
          [outlines]="(outlines$ | async)"
        ></nns-share-item>
      </ng-template>
      <nns-share-uploading
        flex="noshrink"
        *ngIf="(isNoteUploading$ | async)"
        [share]="(share$ | async)"
      ></nns-share-uploading>
      <nns-share-password
        flex="noshrink"
        *ngIf="(isNotePasswordClosed$ | async)"
        [isPasswordWrong]="isPasswordWrong$"
        [visual]="(visual$ | async)"
        [share]="(share$ | async)"
        (onSendPassword)="unlockWithPassword($event)"
      ></nns-share-password>
    </ng-template>
  `,
  styles: [`
    :host {
      -ms-flex:1;
      box-sizing: border-box;
      display: flex;
      flex: 1 1 0;
      -ms-flex-direction: column;
      flex-direction: column;
    }
    :host-context(.no-flex) nns-share-password,
    :host-context(.no-flex) nns-error
     {
       display:block;
     }
  `]
})
export class ShareSmartComponent implements OnInit {
  isNetworkError$;
  isShareFetching$;
  attachments$;
  currentNote$;
  children$;
  login$;
  preview$;
  share$;
  shareCopy$;
  text$;
  todos$;
  visual$;
  isNoteUploading$;
  isNotePasswordClosed$;
  isPasswordWrong$;
  branding$;
  currentOrganization$;
  workspaces$;
  currentNoteWorkspace$;
  profile$;
  outlines$;

  constructor(
    public _route: ActivatedRoute,
    public _store: Store<IAppState>,
    public _meta: Meta,
  ) {
    this.share$ = this._store.pipe(select(selectCurrentShare));
    this.currentNote$ = this._store.pipe(select(selectCurrentNote));
    this.attachments$ = this._store.pipe(select(selectCurrentAttachments));
    this.text$ = this._store.pipe(select(selectText));
    this.children$ = this._store.pipe(select(selectChildren));
    this.login$ = this._store.select<ILoginState>('login');
    this.visual$ = this._store.pipe(select(selectVisual));
    this.shareCopy$ = this._store.pipe(select(selectShareCopy));
    this.preview$ = this._store.pipe(select(selectPreview));
    this.todos$ = this._store.pipe(select(selectTodos));
    this.branding$ = this._store.pipe(select(selectBranding));
    this.workspaces$ = this._store.pipe(select(getCurrentNoteOrgWorkspaces));
    this.currentNoteWorkspace$ = this._store.pipe(select(getCurrentNoteOrgWorkspace));
    this.currentOrganization$ = this._store.pipe(select(getCurrentOrganization))
    this.profile$ = this._store.pipe(select(selectCurrentProfile));
    this.outlines$ = this._store.pipe(select(selectPreparedAllOutlines));

    this.isShareFetching$ = this.isShareFetching();
    this.isNoteUploading$ = this.isNoteUploading();
    this.isNetworkError$ = this.isNetworkError();
    this.isNotePasswordClosed$ = this.isNotePasswordClosed();
    this.isPasswordWrong$ = this.isPasswordWrong();

    combineLatest(
      this.isNetworkError$,
      this.isNotePasswordClosed$,
      this.currentOrganization$
    ).pipe(
      filter(([isNetworkError, isNotePasswordClosed, currentOrganization]) => {
        return (isNetworkError || isNotePasswordClosed) && currentOrganization
      })
    ).subscribe(([isNetworkError, isNotePasswordClosed, currentOrganization]) => {
      this.setMetaForError(currentOrganization)
    })
  }

  ngOnInit() {
    this._route.params.forEach((params: Params) => {
       const nodeId = params.nodeId;
       this._store.dispatch(selectItem(nodeId, true));
     });
  }

  isNoteUploading() {
    return this.share$.pipe(
      map((share:any) => share.isNoteUploading)
    );
  }

  isNotePasswordClosed() {
    return this.share$.pipe(
      map((share:any) => {
        const { instance } = share;
        return instance && instance.passwordRequired && !instance.accessAllowed;
      })
    );
  }

  isNetworkError() {
    return this.share$.pipe(
      map((share:any) => {
        return share.networkErrorCode
      })
    );
  }

  isShareFetching() {
    return this.share$.pipe(
      map((share:any) => share.isFetching && share.isInitialFetched)
    );
  }

  isPasswordWrong() {
    return this.share$.pipe(
      map((share: any) => {
        return share.isPasswordWrong
      })
    );
  }

  setMetaForError(organization) {
    this._meta.setMetaForError(organization);
  }

  unlockWithPassword(password) {
    this._store.dispatch(unlockShare(password));
  }
}
